import React, { useState, useEffect } from "react";
import { FaClipboardList } from "react-icons/fa";
import { FaPlus, FaMinus } from "react-icons/fa";
import handleApiError from "./handleApiError";
import toast from "react-hot-toast";
import authManager from "../../../utils/authManager";
import axios from "axios";
import FormDebugHelper from "../../../utils/formDebugHelper";
import PaymentOptions from "./PaymentOptions";
import { useNavigate } from "react-router-dom";
import { DASHBOARD_ROUTE } from "../../../routes/web/rootRoutes";

const MembershipApplicationArtDept = () => {
  const initialFormState = {
    date: "",
    name: "",
    address: "",
    dob: "",
    contactNo: "",
    email: "",
    bloodGroup: "",
    maritalStatus: "",
    workingWith: "",
    emergencyContact: "",
    experienceYears: "",
    experienceMonths: "",
    workExperience: "",
    references: [{ name: "", memberNo: "" }],
    agree: false,

    // FILES (ROOT LEVEL)
    member_photo: null,
    member_education_qualification: null,
    member_residential_address_proof: null,
    member_recent_photographs: null,
    member_reference_letter: null,
    member_health_certificate: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state
  const [debugInfo, setDebugInfo] = useState(null); // Add debug info state
  const [showPayment, setShowPayment] = useState(false);
  const navigate = useNavigate(); 

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFormData(prev => ({ ...prev, date: today }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0],
      }));
      // Clear error for this field
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      }
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        member_photo: file,
      }));
      setPhotoPreview(URL.createObjectURL(file));
      if (errors.member_photo) {
        setErrors(prev => ({ ...prev, member_photo: undefined }));
      }
    }
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, { name: "", memberNo: "" }],
    }));
  };

  const removeReference = (index) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    let temp = {};

    if (!formData.name?.trim()) temp.name = "Name is required";
    if (!formData.address?.trim()) temp.address = "Address is required";
    const dob = new Date(formData.dob);
    const today = new Date();

    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (!formData.dob) {
      temp.dob = "Date of birth is required";
    } else if (isNaN(dob.getTime())) {
      temp.dob = "Please enter a valid date";
    } else if (dob > today) {
      temp.dob = "Date of birth cannot be in the future";
    } else if (age < 18 || (age === 18 && monthDiff < 0)) {
      temp.dob = "You must be at least 18 years old";
    }

    // Contact validations
    if (!formData.contactNo?.trim()) {
      temp.contactNo = "Contact number is required";
    } else if (!/^[0-9]{10}$/.test(formData.contactNo)) {
      temp.contactNo = "Valid 10-digit contact number required";
    }

    if (!formData.email?.trim()) {
      temp.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      temp.email = "Valid email required";
    }

    if (!formData.bloodGroup?.trim()) temp.bloodGroup = "Blood group required";
    if (!formData.maritalStatus) temp.maritalStatus = "Marital status required";
    if (!formData.workingWith?.trim()) temp.workingWith = "Working organization required";

    if (!formData.emergencyContact?.trim()) {
      temp.emergencyContact = "Emergency contact is required";
    } else if (!/^[0-9]{10}$/.test(formData.emergencyContact)) {
      temp.emergencyContact = "Valid emergency contact required";
    }

    // Experience validation
    if (!formData.experienceYears && !formData.experienceMonths) {
      temp.experience = "Experience required";
    }

    if (!formData.workExperience?.trim()) {
      temp.workExperience = "Work experience required";
    }

    // File validations
    if (!formData.member_photo) temp.member_photo = "Applicant photo required";
    if (!formData.member_education_qualification) temp.member_education_qualification = "Qualification file required";
    if (!formData.member_residential_address_proof) temp.member_residential_address_proof = "Address proof required";
    if (!formData.member_recent_photographs) temp.member_recent_photographs = "Recent photographs required";
    if (!formData.member_reference_letter) temp.member_reference_letter = "Reference letter required";
    if (!formData.member_health_certificate) temp.member_health_certificate = "Health certificate required";

    // References validation
    formData.references.forEach((ref, i) => {
      if (!ref.name?.trim() || !ref.memberNo?.trim()) {
        temp[`ref${i}`] = `Reference ${i + 1} details required`;
      }
    });

    // Agreement validation
    if (!formData.agree) temp.agree = "You must agree before submitting";

    return temp;
  };

  // Debug function to check form before submission
  const debugForm = () => {
    console.log('🔍 Running pre-submission debug...');

    // Check environment
    const apiOk = FormDebugHelper.checkApiConfig();
    const authOk = FormDebugHelper.checkAuth();

    const validation = FormDebugHelper.validateFormData(formData);

    setDebugInfo({
      apiConfigured: apiOk,
      authenticated: authOk,
      validationErrors: validation.errors,
      validationWarnings: validation.warnings,
      formDataSnapshot: { ...formData }
    });

    return validation.isValid && apiOk && authOk;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run debug first
    const isReady = debugForm();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Show detailed error toast
      toast.error(`Please fix ${Object.keys(validationErrors).length} errors`);
      return;
    }

    if (!isReady) {
      toast.error("Form validation failed. Check console for details.");
      return;
    }

    setIsSubmitting(true);

    try {
      const form = new FormData();

      // Append all fields with correct names
      form.append("date", formData.date);
      form.append("name", formData.name);
      form.append("address", formData.address);
      form.append("dob", formData.dob);
      form.append("contact_number", formData.contactNo);
      form.append("emergency_contact_number", formData.emergencyContact);
      form.append("email", formData.email);
      form.append("blood_group", formData.bloodGroup);
      form.append("married_status", formData.maritalStatus);
      form.append("work_with", formData.workingWith);
      form.append("experience_years", formData.experienceYears || "0");
      form.append("experience_months", formData.experienceMonths || "0");
      form.append("work_experience", formData.workExperience);
      form.append("form_type", "ad");

      // Append references as JSON string to avoid formatting issues
      const referencesData = formData.references.map(ref => ({
        name: ref.name,
        memberNo: ref.memberNo
      }));
      form.append("references", JSON.stringify(referencesData));

      // Append files (check if they exist)
      if (formData.member_photo) form.append("member_photo", formData.member_photo);
      if (formData.member_education_qualification) form.append("member_education_qualification", formData.member_education_qualification);
      if (formData.member_residential_address_proof) form.append("member_residential_address_proof", formData.member_residential_address_proof);
      if (formData.member_recent_photographs) form.append("member_recent_photographs", formData.member_recent_photographs);
      if (formData.member_reference_letter) form.append("member_reference_letter", formData.member_reference_letter);
      if (formData.member_health_certificate) form.append("member_health_certificate", formData.member_health_certificate);

      FormDebugHelper.logFormData(form);

      const token = authManager.getAccessToken();
      // if (!token) {
      //   toast.error("Authentication token not found. Please login again.");
      //   setIsSubmitting(false);
      //   return;
      // }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        toast.error("API URL not configured");
        setIsSubmitting(false);
        return;
      }

      console.log(`📤 Sending request to: ${apiUrl}new-ad`);

      const response = await axios.post(
        `${apiUrl}new-ad`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000, // 30 seconds timeout for file uploads
        }
      );

      console.log('✅ Response received:', response.data);
      toast.success("Membership Application submitted successfully!");
      setTimeout(() => {
            navigate(DASHBOARD_ROUTE);

        setShowPayment(true);
      }, 1000);

      // Reset form after successful submission
      setFormData(initialFormState);
      setPhotoPreview(null);
      setErrors({});
      setDebugInfo(null);

    } catch (error) {
      console.error('❌ Submission error:', error);

      // Enhanced error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);

        toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
        toast.error('No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
        toast.error(`Error: ${error.message}`);
      }

      handleApiError(error, setErrors, "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Debug panel component
  const DebugPanel = () => {
    if (!debugInfo) return null;

    return (
      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
        <h4 className="font-bold text-yellow-800 mb-2">🔧 Debug Information</h4>
        <div className="text-sm">
          <p>API Configured: {debugInfo.apiConfigured ? '✅' : '❌'}</p>
          <p>Authenticated: {debugInfo.authenticated ? '✅' : '❌'}</p>
          {debugInfo.validationErrors.length > 0 && (
            <div>
              <p className="font-semibold text-red-600">Validation Errors:</p>
              <ul className="list-disc pl-5">
                {debugInfo.validationErrors.map((err, i) => (
                  <li key={i} className="text-red-600">{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };


  if (showPayment) {
    return (
      <>
        {showPayment && (
          <PaymentOptions
          />
        )}
      </>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-5xl mx-auto my-10 bg-white shadow-lg rounded-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#6a11cb] to-[#2575fc] text-white text-center py-6 rounded-t-xl">
          <h1 className="text-2xl font-semibold">NEW MEMBERSHIP APPLICATION ART DEPARTMENT</h1>
        </div>

        <div className="p-2 sm:p-10">
          {/* Add Debug Panel */}
          <DebugPanel />

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Name & Date */}
            <div className="grid grid-cols-1 gap-5 p-5 rounded-lg shadow-sm sm:grid-cols-2 bg-blue-50">
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Applicant Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full px-3 py-1 bg-white border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  readOnly
                  className="w-full px-3 py-1 text-gray-700 bg-gray-100 border rounded-lg outline-none cursor-not-allowed focus:ring-0"
                />
              </div>
            </div>

            {/* To Section */}
            <div className="p-4 text-gray-700 border-l-4 border-blue-500 rounded-lg shadow-sm bg-gray-50">
              <p className="font-semibold">To,</p>
              <p>The President / General Secretary</p>
              <p>Association of Cine & Television Art Directors & Costume Designers</p>
            </div>

            {/* Personal Details */}
            <section className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Residential Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter residential address"
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.address && (
                  <p className="mt-1 text-xs text-red-500">{errors.address}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.dob && (
                  <p className="mt-1 text-xs text-red-500">{errors.dob}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Contact No
                </label>
                <input
                  type="tel"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleChange}
                  placeholder="Enter 10-digit contact number"
                  maxLength="10"
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.contactNo && (
                  <p className="mt-1 text-xs text-red-500">{errors.contactNo}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Email ID
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Blood Group
                </label>
                <input
                  type="text"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                  placeholder="e.g., O+, A-"
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.bloodGroup && (
                  <p className="mt-1 text-xs text-red-500">{errors.bloodGroup}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Marital Status
                </label>
                <select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleChange}
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select</option>
                  <option value="Married">Married</option>
                  <option value="Unmarried">Unmarried</option>
                </select>
                {errors.maritalStatus && (
                  <p className="mt-1 text-xs text-red-500">{errors.maritalStatus}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Presently Working With
                </label>
                <input
                  type="text"
                  name="workingWith"
                  value={formData.workingWith}
                  onChange={handleChange}
                  placeholder="Enter company or organization"
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.workingWith && (
                  <p className="mt-1 text-xs text-red-500">{errors.workingWith}</p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-700">
                  Emergency Contact No
                </label>
                <input
                  type="tel"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  placeholder="Enter emergency contact"
                  maxLength="10"
                  className="w-full px-3 py-1 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.emergencyContact && (
                  <p className="mt-1 text-xs text-red-500">{errors.emergencyContact}</p>
                )}
              </div>
            </section>

            {/* Experience */}
            <section>
              <label className="block mb-2 font-semibold text-gray-700">
                For how long you have been in this industry
              </label>
              <div className="flex flex-wrap gap-3">
                <input
                  type="number"
                  name="experienceYears"
                  placeholder="Years"
                  value={formData.experienceYears}
                  onChange={handleChange}
                  min="0"
                  className="w-24 px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  name="experienceMonths"
                  placeholder="Months"
                  value={formData.experienceMonths}
                  onChange={handleChange}
                  min="0"
                  max="11"
                  className="w-24 px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {errors.experience && (
                <p className="mt-1 text-xs text-red-500">{errors.experience}</p>
              )}
            </section>

            {/* Work Experience */}
            <section>
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Work Experiences
              </label>
              <textarea
                name="workExperience"
                value={formData.workExperience}
                onChange={handleChange}
                rows="3"
                className="w-full p-3 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your experience"
              ></textarea>
              {errors.workExperience && (
                <p className="mt-1 text-xs text-red-500">{errors.workExperience}</p>
              )}
            </section>

            {/* Upload Photo */}
            <section className="p-4 border border-red-200 rounded-lg shadow-sm bg-red-50 w-fit">
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Upload Photo
              </label>
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="p-2 text-sm border border-gray-300 rounded-lg"
                />
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="object-cover w-24 h-24 border rounded-lg"
                  />
                )}
              </div>
              {errors.member_photo && (
                <p className="mt-1 text-xs text-red-500">{errors.member_photo}</p>
              )}
            </section>

            {/* References Section */}
            <section className="p-5 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-md">References</h3>
                <button
                  type="button"
                  onClick={addReference}
                  className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800"
                >
                  <FaPlus className="mr-1" /> Add Reference
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-300 rounded-lg">
                  <thead>
                    <tr className="text-left bg-gray-100">
                      <th className="w-20 px-3 py-2 border-b border-gray-300">Sr. No.</th>
                      <th className="px-3 py-2 border-b border-gray-300">Name</th>
                      <th className="px-3 py-2 border-b border-gray-300">Member No.</th>
                      <th className="w-20 px-3 py-2 border-b border-gray-300">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.references.map((ref, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-center border-b border-gray-300">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-300">
                          <input
                            type="text"
                            value={ref.name}
                            onChange={(e) => {
                              const updatedRefs = [...formData.references];
                              updatedRefs[index].name = e.target.value;
                              setFormData((prev) => ({ ...prev, references: updatedRefs }));
                              // Clear reference error
                              if (errors[`ref${index}`]) {
                                setErrors(prev => ({ ...prev, [`ref${index}`]: undefined }));
                              }
                            }}
                            placeholder="Enter name"
                            className="w-full px-2 py-1 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 border-b border-gray-300">
                          <input
                            type="text"
                            value={ref.memberNo}
                            onChange={(e) => {
                              const updatedRefs = [...formData.references];
                              updatedRefs[index].memberNo = e.target.value;
                              setFormData((prev) => ({ ...prev, references: updatedRefs }));
                              // Clear reference error
                              if (errors[`ref${index}`]) {
                                setErrors(prev => ({ ...prev, [`ref${index}`]: undefined }));
                              }
                            }}
                            placeholder="Enter membership number"
                            className="w-full px-2 py-1 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-center border-b border-gray-300">
                          {formData.references.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeReference(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FaMinus />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {formData.references.map((_, i) =>
                errors[`ref${i}`] ? (
                  <p key={i} className="mt-1 text-xs text-red-500">
                    {errors[`ref${i}`]}
                  </p>
                ) : null
              )}
            </section>

            {/* Documents Required Section */}
            <div className="p-4 border border-blue-200 rounded-lg shadow-sm bg-blue-50">
              <h3 className="mb-4 text-lg font-semibold text-blue-700">
                📄 Required Documents
              </h3>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    1. Education / Technical Qualification Degree / Diploma
                  </label>
                  <input
                    type="file"
                    name="member_education_qualification"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-green-300 rounded"
                  />
                  {errors.member_education_qualification && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.member_education_qualification}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    2. Residential Address Proof (Aadhar / Passport / Ration Card)
                  </label>
                  <input
                    type="file"
                    name="member_residential_address_proof"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-green-300 rounded"
                  />
                  {errors.member_residential_address_proof && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.member_residential_address_proof}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    3. Recent Passport Size Photographs (2 Nos.)
                  </label>
                  <input
                    type="file"
                    name="member_recent_photographs"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-green-300 rounded"
                  />
                  {errors.member_recent_photographs && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.member_recent_photographs}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    4. Reference Letter from 3 Art Directors (Members Only)
                  </label>
                  <input
                    type="file"
                    name="member_reference_letter"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-green-300 rounded"
                  />
                  {errors.member_reference_letter && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.member_reference_letter}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    5. Health Certificate
                  </label>
                  <input
                    type="file"
                    name="member_health_certificate"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-green-300 rounded"
                  />
                  {errors.member_health_certificate && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.member_health_certificate}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <section className="p-5 border border-blue-200 rounded-lg shadow-sm bg-blue-50">
              <h3 className="mb-3 font-semibold text-blue-700">
                📌 Terms and Conditions
              </h3>

              <ul className="space-y-2 text-sm leading-relaxed text-gray-700 list-decimal list-inside">
                <li>
                  Once the applicant for membership has been approved by the executive
                  committee, the applicant must comply with all payment formalities within
                  one month.
                </li>
                <li>Amount paid by the applicant is non-refundable.</li>
                <li>
                  If the applicant uses the installment facility, he/she must submit all
                  Post-Dated Cheques (PDC) along with the application.
                </li>
                <li>
                  PDCs submitted by the applicant must be duly honored. If any PDC is not
                  realized for more than two months, the application will be treated as
                  cancelled. The amount paid will be forfeited and transferred to the
                  ACTADCD welfare fund. A fresh application will be required.
                </li>
                <li>
                  ACTADCD will not entertain any dispute from the applicant until a
                  membership number is issued. The applicant will not have voting rights
                  until he/she receives the membership number.
                </li>
                <li>
                  Membership card and ACTADCD benefits will be provided **only after full
                  payment is completed**.
                </li>
                <li>All payments should be made in favor of **ACTADCD**.</li>
              </ul>

              {/* Agree checkbox */}
              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                  className="w-4 h-4 mr-2"
                />
                <p className="text-sm text-gray-700">
                  I agree to the Terms & Conditions.
                </p>
              </div>

              {errors.agree && (
                <p className="mt-1 text-xs text-red-500">{errors.agree}</p>
              )}
            </section>

            {/* Submit */}
            <div className="text-right">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-gradient-to-r from-[#6a11cb] to-[#2575fc] text-white font-medium px-8 py-2 rounded-lg shadow-md ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MembershipApplicationArtDept;